import type { OfflineSceneKey, OfflineViewState, View } from "@novorender/api";

// Deep import for the same reason `bimIndexDataAccess` uses one: the `apis/procore`
// barrel would pull the RTK Query API and the analytics client in behind one probe.
import type { BimQueryTarget } from "apis/procore/bimQuery";
import { BimIndexDataAccess, bimIndexDir, BimIndexOfflineDataAccess, probeBimIndex } from "features/bimIndex";
import type { AppObjectDB } from "utils/appObjectDb";

import { ObjectDbDataAccess } from "./objectDbDataAccess";
import type { ObjectDataAccess } from "./types";

/**
 * Choose the backend for this session, once, at startup.
 *
 * In order of preference: a **downloaded** BIM index when the scene has one, since it
 * answers hierarchy and property queries in-process with no network at all; then a
 * **served** BIM index when there is a target to address, which is the same engine and the
 * same query algebra over HTTP, and so available without a sync — used without a probe, so
 * a version still backfilling surfaces as `SceneBackfillingError` from its reads; then the object DB,
 * which is always available. Every method the interface promises has to be answerable
 * from all three, which is why the fallback is a whole implementation rather than a
 * per-method one.
 *
 * Deliberately not re-decided later. A consumer holds the interface for the life of
 * the session and an index that appears mid-session (a sync finishing in another tab,
 * a backfill completing) would otherwise swap the backend under it, so a scene that
 * gains one while open uses it from the next load instead.
 *
 * Can override dataAccess in DEV via URL by passing dataAccess=object-db|bim-index|bim-index-offline
 * bim-index-offline runs only if an offline index is available.
 * Overriding dataAccess to "object-db" or "bim-index" will not use offline data.
 * An override to "bim-index-offline" that finds no usable offline index stops at the
 * object DB rather than falling through to the served one, so debugging offline
 * behaviour is not silently masked by a working network backend.
 */
export async function pickDataAccess(
    db: AppObjectDB,
    view: View,
    offlineWorkerState: OfflineViewState | undefined,
    enableBimIndex: boolean,
    servedIndex?: BimQueryTarget,
): Promise<ObjectDataAccess> {
    // A slice prefers an index whether or not the host opted into one: the object DB only
    // narrows to a slice on its FAS reads, and its search-DB endpoints still answer for
    // the whole version (BM-13591).
    const hasSliceId = Boolean(servedIndex?.sceneSliceId);
    let preferredDataAccess: ObjectDataAccess["kind"] =
        enableBimIndex || hasSliceId ? "bim-index-offline" : "object-db";
    let overridden = false;

    if (
        import.meta.env.DEV &&
        typeof window !== "undefined" &&
        ["object-db", "bim-index", "bim-index-offline"].includes(
            new URLSearchParams(window.location.search).get("dataAccess") || "",
        )
    ) {
        preferredDataAccess = new URLSearchParams(window.location.search).get("dataAccess") as ObjectDataAccess["kind"];
        overridden = true;
        console.info(`Preferred data access override: ${preferredDataAccess}`);

        // All data sources use view.data for offline if it's available.
        // If dataAccess is overridden - I don't want online engines to use offline data.
        if (preferredDataAccess !== "bim-index-offline") {
            view = { ...view, data: undefined } as unknown as View;
        }
    }

    if (preferredDataAccess === "object-db") {
        if (import.meta.env.DEV) {
            console.info("BIM index is disabled: using the object DB");
        }
        return new ObjectDbDataAccess(db, view);
    }

    // Looking for an accelerator must never cost the scene. This is awaited inline in
    // `useHandleInit`, where anything thrown lands in the init catch and becomes
    // `sceneStatus: Error` — so a scene that renders perfectly well from the object DB
    // would fail to open. `probeBimIndex` guards its own OPFS walk, but `getOPFSPath`
    // is the SDK's and rejects a path it will not resolve. Same shape as `getOfflineViewState`
    // directly above the call site.
    if (preferredDataAccess === "bim-index-offline" && servedIndex?.sceneId) {
        const sceneKey: OfflineSceneKey = {
            sceneId: servedIndex.sceneId,
            sceneVersionId: servedIndex.sceneVersionId,
            sceneSliceId: servedIndex.sceneSliceId,
        };
        const availability = await probeOfflineSceneBimIndex(offlineWorkerState, sceneKey).catch((e) => {
            console.warn("pickDataAccess: could not look for an offline BIM index:", e);
            return undefined;
        });
        if (availability?.usable) {
            if (import.meta.env.DEV) {
                console.info(`Offline BIM index at ${availability.dir}: using it for object data access`);
            }
            // Nothing is opened here — the worker starts on the first query, so a session
            // that asks nothing of it pays nothing for having chosen it.
            return new BimIndexOfflineDataAccess(view, { dir: availability.dir });
        }
        if (overridden) {
            // Asked for by name, so the served index is not a substitute: whoever set
            // the override is looking at offline behaviour, and a working network
            // backend would hide the fact that there was nothing to look at.
            console.error(
                "pickDataAccess: ?dataAccess=bim-index-offline found no usable offline index; using the object DB rather than the served index",
            );
            return new ObjectDbDataAccess(db, view);
        }
    }

    // No need to preferredDataAccess="bim-index" - the branch will catch both bim-index and bim-index-offline.
    if (servedIndex) {
        if (import.meta.env.DEV) {
            console.info(
                `Served BIM index for scene version ${servedIndex.sceneVersionId}: using it for object data access`,
            );
        }
        return new BimIndexDataAccess(view, servedIndex);
    }

    return new ObjectDbDataAccess(db, view);
}

/**
 * What the scene's offline directory holds, or `undefined` if the scene has no
 * offline directory to hold anything.
 *
 * The offline state is what knows where a scene's files are, so an index can only be
 * found through it: the scene's `getOPFSPath` names the directory, and the probe says
 * what is in it. A scene the offline state does not have was never made available
 * offline, and a missing offline state means the browser gave the view no offline storage
 * at all — neither can have an index, and neither is worth a probe.
 */
async function probeOfflineSceneBimIndex(offlineWorkerState: OfflineViewState | undefined, sceneKey: OfflineSceneKey) {
    const scene = offlineWorkerState?.getScene(sceneKey);
    if (!scene) {
        return undefined;
    }
    const availability = await probeBimIndex(bimIndexDir(scene));
    if (availability.present.length && !availability.usable) {
        // Some of an index but not enough: an interrupted download, or an archive
        // built without a section this needs. Worth saying out loud — it falls back
        // to the object DB and otherwise looks like a scene that never had one.
        console.warn(`Incomplete BIM index at ${availability.dir}, missing ${availability.missing.join(", ")}`);
    }
    return availability;
}
